import React, { useMemo, useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

function colorFor(ratio) {
  if (ratio >= 0.66) return { c: '#ef4444', f: '#fca5a5' };
  if (ratio >= 0.33) return { c: '#d97706', f: '#fcd34d' };
  return { c: '#0f766e', f: '#6ee7b7' };
}

/**
 * Carte d'activité prévue par heure : un curseur (0–23 h) anime l'intensité
 * d'activité de chaque arrondissement (rayon + couleur ∝ flux prévu à cette heure).
 */
export default function ActivityMap({ data }) {
  const [hour, setHour] = useState(8);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);

  const { zones, center, maxVal } = useMemo(() => {
    const z = (data?.zones || []).filter((x) => x.lat != null && x.lon != null && x.hourly);
    const c = z.length
      ? [z.reduce((s, x) => s + x.lat, 0) / z.length, z.reduce((s, x) => s + x.lon, 0) / z.length]
      : [14.72, -17.45];
    const mv = Math.max(1, ...z.flatMap((x) => x.hourly));
    return { zones: z, center: c, maxVal: mv };
  }, [data]);

  useEffect(() => {
    if (playing) {
      timer.current = setInterval(() => setHour((h) => (h + 1) % 24), 700);
    }
    return () => clearInterval(timer.current);
  }, [playing]);

  return (
    <div className="mapcard reveal">
      <div className="mapcard__head">
        <div className="panel__title">
          <span className="ic">🕒</span>
          <div>
            <h3>Carte d'affluence prévue par heure</h3>
            <div className="panel__sub">Profil horaire réel (D4D) — intensité par arrondissement</div>
          </div>
        </div>
        <span className="panel__badge">{data?.source === 'd4d' ? 'Données réelles' : 'Démo'}</span>
      </div>

      <div className="hour-bar">
        <button className="hour-bar__play" onClick={() => setPlaying((p) => !p)}>
          {playing ? '❚❚' : '▶'}
        </button>
        <input className="range" type="range" min="0" max="23" value={hour} onChange={(e) => { setPlaying(false); setHour(Number(e.target.value)); }} />
        <span className="hour-bar__label mono">{String(hour).padStart(2, '0')}:00</span>
      </div>

      <div className="mapcard__map" style={{ height: 460 }}>
        <MapContainer center={center} zoom={11} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution="&copy; OpenStreetMap, &copy; CARTO" subdomains="abcd" maxZoom={19}
          />
          {zones.map((z) => {
            const v = z.hourly[hour] || 0;
            const ratio = v / maxVal;
            const col = colorFor(ratio);
            return (
              <CircleMarker
                key={z.zone_id}
                center={[z.lat, z.lon]}
                radius={6 + Math.sqrt(ratio) * 26}
                pathOptions={{ color: col.c, fillColor: col.f, fillOpacity: 0.65, weight: 2 }}
              >
                <Tooltip direction="top" offset={[0, -4]}>
                  <strong>{z.nom}</strong><br />{v.toLocaleString('fr-FR')} à {String(hour).padStart(2, '0')}:00
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
